import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import {
  Database,
  Download,
  CheckCircle2,
  XCircle,
} from "@/components/icons";

/**
 * Doctor "Base de datos" page — two giant action tiles:
 *   • Subir base de datos  → hidden file input + real upload progress
 *   • Descargar base de datos → streams the .xlsx backup
 */
export default function DoctorDatabase() {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);

  const onPickFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Solo se aceptan archivos .xlsx");
      return;
    }
    setUploading(true);
    setProgress(0);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post("/database/import-patients", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
      setProgress(100);
      setResult({ ok: true, ...r.data });
      toast.success(
        `Importados ${r.data.imported} pacientes (${r.data.skipped} saltados)`
      );
    } catch (err) {
      setResult({ ok: false, error: formatErr(err.response?.data?.detail) });
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/database/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consudoctor-backup-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descarga lista");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-7" data-testid="doctor-database-page">
      <div className="flex items-center gap-3">
        <Database className="w-9 h-9 text-black" />
        <div>
          <h1 className="text-2xl font-extrabold text-black tracking-tight">
            Base de datos
          </h1>
          <p className="text-sm text-black/55">
            Sube tu base desde otra plataforma o descarga una copia de seguridad.
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={(e) => onPickFile(e.target.files?.[0])}
        data-testid="db-file-input"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
        {/* Subir */}
        <button
          type="button"
          onClick={() => !uploading && inputRef.current?.click()}
          disabled={uploading}
          data-testid="db-upload-tile"
          className="group bg-[#F5F5F5] hover:bg-white rounded-[28px] p-8 text-center transition disabled:opacity-70 disabled:cursor-progress"
        >
          <Database className="w-20 h-20 text-black mx-auto mb-6 group-hover:scale-110 transition" />
          <div className="text-lg font-extrabold text-black tracking-tight">
            Subir base de datos
          </div>
          <div className="text-xs text-black/55 mt-1">
            {uploading
              ? `Subiendo… ${progress}%`
              : "Selecciona un archivo .xlsx"}
          </div>
          {(uploading || progress > 0) && (
            <div className="mt-4 h-1.5 bg-white rounded-full overflow-hidden">
              <div
                data-testid="db-progress-bar"
                className="h-full bg-[#01BC8E] transition-all duration-200 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </button>

        {/* Descargar */}
        <button
          type="button"
          onClick={onDownload}
          disabled={downloading}
          data-testid="db-download-tile"
          className="group bg-[#F5F5F5] hover:bg-white rounded-[28px] p-8 text-center transition disabled:opacity-70 disabled:cursor-progress"
        >
          <Download className="w-20 h-20 text-black mx-auto mb-6 group-hover:scale-110 transition" />
          <div className="text-lg font-extrabold text-black tracking-tight">
            Descargar base de datos
          </div>
          <div className="text-xs text-black/55 mt-1">
            {downloading ? "Preparando archivo…" : "Genera un .xlsx con tus datos"}
          </div>
        </button>
      </div>

      {result && (
        <div className="max-w-2xl">
          {result.ok ? (
            <div
              className="rounded-2xl bg-blue-50 border border-emerald-200 p-4 flex items-start gap-3"
              data-testid="db-result-ok"
            >
              <CheckCircle2 className="w-5 h-5 text-[#019A75] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-emerald-800">
                <strong>{result.imported}</strong> pacientes importados ·{" "}
                <strong>{result.skipped}</strong> saltados de un total de{" "}
                <strong>{result.total}</strong>.
              </div>
            </div>
          ) : (
            <div
              className="rounded-2xl bg-red-50 border border-red-200 p-4 flex items-start gap-3"
              data-testid="db-result-err"
            >
              <XCircle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-800">{result.error}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
